import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Typography,
  Paper,
  Container,
  Grid,
  Button,
  Card,
  CardContent,
  Divider,
  Box,
  Tabs,
  Tab
} from '@mui/material';
import { Link } from 'react-router-dom';
import LoadingIndicator from '../components/LoadingIndicator';
import IndiceFavorabilidade from '../components/IndiceFavorabilidade';
import SwotRadarChart from '../components/SwotRadarChart';
import { useNotificador } from '../utils/notificador';
import {
  PlannedActionsCard,
  NotStartedActionsCard,
  InProgressActionsCard,
  CompletedActionsCard,
  AcoesPorAreaChart,
  SwotHistogramChart,
  InsightsCard
} from '../components/dashboard';
import OKRList from '../components/OKRList';
import PLRList from '../components/PLRList';

import ReactPlayer from 'react-player';
import { Info, TrendingUp, Assessment, Dashboard as DashboardIcon, Map } from '@mui/icons-material';
import BasicModal from '../components/modais/BasicModal';
import MapaEstrategico from './MapaEstrategico';
import { flex } from '@mui/system';

const Dashboard: React.FC = () => {
  const authContext = useAuth();
  const [loading] = useState<boolean>(false);
  const { enviarNotificacao, tipos } = useNotificador();
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  const enviarNotificacaoTeste = () => {
    enviarNotificacao(
      'Teste de Notificação',
      'Esta é uma notificação de teste enviada pelo Dashboard',
      tipos.INFO,
      '/tw/notificacoes',
      'Ver notificações'
    );
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const renderTabContent = () => {
    switch (tabValue) {
      case 0:
        return renderDashboardContent();
      case 1:
        return <OKRList />;
      case 2:
        return <PLRList />;
      case 3:
        return <MapaEstrategico />
      default:
        return renderDashboardContent();
    }
  };

  const renderDashboardContent = () => (
    <>
      <Grid size={{ xs: 12, md: 12, lg: 12 }} sx={{ mb: 3 }}>
        <InsightsCard />
      </Grid>

      {/* Action Statistics Cards - Top Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
          <PlannedActionsCard />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
          <NotStartedActionsCard />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
          <InProgressActionsCard />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
          <CompletedActionsCard />
        </Grid>
      </Grid>

      {/* Key Performance Indicators */}
      <Grid size={{ xs: 12, md: 12 }}>
        <Paper
          elevation={2}
          sx={{
            p: 3,
            height: '100%',
            borderRadius: 2
          }}
        >
          <Typography variant="h5" fontWeight="medium" gutterBottom>
            Análise de Desempenho
          </Typography>
          <Divider sx={{ my: 2 }} />

          {loading ? (
            <LoadingIndicator message="Carregando informações..." />
          ) : (
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 12, lg: 12 }} >
                <Card elevation={0} sx={{ mb: 3 }}>
                  <CardContent sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="h6" gutterBottom>
                      Índice de Favorabilidade
                    </Typography>
                    <IndiceFavorabilidade />
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                <Card elevation={0}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom align="center">
                      Análise SWOT (Radar)
                    </Typography>
                    <SwotRadarChart />
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                <Card elevation={0}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom align="center">
                      Análise SWOT (Histograma)
                    </Typography>
                    <SwotHistogramChart />
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                <Card elevation={0}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom align="center">
                      Ações por Área
                    </Typography>
                    <AcoesPorAreaChart />
                  </CardContent>
                </Card>
              </Grid>

            </Grid>
          )}
        </Paper>
      </Grid>
    </>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3 } }}>
      <Grid container spacing={3}>
        {/* Welcome + Tabs unificado */}
        <Grid size={{ xs: 12 }}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}
          >
            {/* Cabeçalho de boas-vindas */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                gap: 2,
                mb: 2
              }}
            >
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="h4" fontWeight="medium">
                    Bem-vindo, {authContext.user?.name}!
                  </Typography>
                  <Button
                    variant="contained"
                    sx={{
                      height: 30,
                      minWidth: 0,
                      paddingInline: '8px',
                      paddingBlock: '12px',
                      borderRadius: 1.8
                    }}
                    onClick={() => setIsVideoModalOpen(true)}
                  >
                    <Info sx={{ fontSize: 16 }} />
                  </Button>
                </Box>
                <Typography variant="subtitle1" color="text.secondary">
                  Aqui está o resumo das suas atividades e métricas
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  component={Link}
                  to="/tw/profile"
                  size="small"
                >
                  Gerenciar Perfil
                </Button>
              </Box>
            </Box>

            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="dashboard tabs"
              sx={{
                minHeight: 40,
                '& .MuiTab-root': {
                  p: 0,
                  minHeight: 40,
                  minWidth: 'auto',
                  marginRight: 4,
                },
                '& .MuiTab-root:last-of-type': {
                  marginRight: 0,
                }
              }}
            >
              <Tab label="Dashboard" icon={<DashboardIcon />} iconPosition="start" />
              <Tab label="OKRs" icon={<TrendingUp />} iconPosition="start" />
              <Tab label="PLR" icon={<Assessment />} iconPosition="start" />
              <Tab label="Mapa Estratégico" icon={<Map />} iconPosition="start" />
            </Tabs>
          </Paper>
        </Grid>

        {/* Tab Content */}
        <Grid size={{ xs: 12 }}>
          {renderTabContent()}
        </Grid>
      </Grid>
      <BasicModal
        title='Entenda a aba Dashboard'
        open={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        sizeContent="medium"
      >
        <Box sx={{ position: 'relative', paddingTop: '56.2%' }}>
          <ReactPlayer
            url='https://youtu.be/BN4bUlsvwmk'
            width='100%'
            height='100%'
            style={{ position: 'absolute', top: 0, left: 0 }}
            controls
          />
        </Box>
      </BasicModal>
    </Container>
  );
};

export default Dashboard;